<script lang="ts">
	import {
		DIVIDER_FOR_W_TO_KW,
		getAccommodationSubTypeNames,
	} from '$lib/components/pois/constants';
	import {
		buildAccommodationMichelinDistinction,
		buildAccommodationPriceText,
		buildAveragePriceText,
		buildRestaurantMichelinDistinction,
		buildReviewText,
		buildTourismMichelinDistinction,
		getRestStopHeadlineText,
	} from '$lib/components/pois/content-utils';
	import DatasheetHighlightRestStopServices from '$lib/components/pois/datasheet/highlight/DatasheetHighlightRestStopServices.svelte';
	import DatasheetHighlightTemplate from '$lib/components/pois/datasheet/highlight/DatasheetHighlightTemplate.svelte';
	import { type DatasheetPoiData } from '$lib/components/pois/types';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';

	interface Props {
		poi: DatasheetPoiData;
		displayPicture?: boolean;
	}

	const { poi, displayPicture = true }: Props = $props();

	const accommodationSubtypeNames = getAccommodationSubTypeNames();
	const searchParams = getSearchParamsStore();
</script>

{#if poi.__typename === 'AccommodationPOI'}
	<DatasheetHighlightTemplate
		scheme="hotel"
		imageUrl={poi.images[0]?.w160}
		priceCategory={poi.priceCategory ?? undefined}
		priceText={$searchParams.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE
			? buildAccommodationPriceText(poi)
			: undefined}
		reviewText={buildReviewText(poi.review)}
		subTypeText={accommodationSubtypeNames[poi.subType as keyof typeof accommodationSubtypeNames]}
		{displayPicture}
	/>
{:else if poi.__typename === 'AccommodationMichelinPOI'}
	<DatasheetHighlightTemplate
		scheme="hotelMichelin"
		imageUrl={poi.images[0]?.w160}
		michelinDistinction={buildAccommodationMichelinDistinction(poi)}
		priceCategory={poi.priceCategory ?? undefined}
		priceText={$searchParams.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE
			? buildAccommodationPriceText(poi)
			: undefined}
		subTypeText={accommodationSubtypeNames[poi.subType as keyof typeof accommodationSubtypeNames]}
		{displayPicture}
	/>
{:else if poi.__typename === 'RestaurantMichelinPOI'}
	<DatasheetHighlightTemplate
		scheme="restaurantMichelin"
		imageUrl={poi.images[0]?.w160}
		michelinDistinction={buildRestaurantMichelinDistinction(poi)}
		hasGreenStar={poi.greenStar}
		priceCategory={poi.priceCategory ?? undefined}
		subTypeText={poi.meals?.cuisineTypes[0] ?? undefined}
		{displayPicture}
	/>
{:else if poi.__typename === 'RestaurantPOI'}
	<DatasheetHighlightTemplate
		scheme="restaurant"
		imageUrl={poi.images[0]?.w160}
		reviewText={buildReviewText(poi.review)}
		priceText={buildAveragePriceText(poi.averagePrice)}
		{displayPicture}
	/>
{:else if poi.__typename === 'TourismPOI'}
	<DatasheetHighlightTemplate
		scheme="tourism"
		imageUrl={poi.images[0]?.w160}
		michelinDistinction={buildTourismMichelinDistinction(poi)}
		subTypeText={poi.classificationType}
		{displayPicture}
	/>
{:else if poi.__typename === 'ServiceEVPOI'}
	<DatasheetHighlightTemplate
		scheme="electric"
		evData={{
			spotCount:
				poi.spots_count === 1 ? m.ev_spots__one() : m.ev_spots__many({ count: poi.spots_count }),
			connectorHighlight: poi.connectors.map(connector => connector.name).join(' · '),
			maxPower:
				Math.max(
					...poi.connectors.flatMap(connector => connector.spots.map(spot => spot.power)),
				) / DIVIDER_FOR_W_TO_KW,
		}}
	/>
{:else if poi.__typename === 'ServiceRestStopPOI'}
	{#if poi.facilities && poi.facilities.length > 0}
		<DatasheetHighlightRestStopServices
			sponsorId={poi.isFamilyFriendly ? 'kleber' : undefined}
			headline={getRestStopHeadlineText(poi)}
			facilities={poi.facilities}
		/>
	{:else}
		<DatasheetHighlightTemplate
			scheme="rest"
			subTypeText={poi.variant === 'REST'
				? m.poi_datasheet__highlight__rest_stop__rest()
				: m.poi_datasheet__highlight__rest_stop__service()}
		/>
	{/if}
{/if}
